import { AfterContentChecked, ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { StepperNavigationService, VentaService } from '../venta';
import { ModalService } from 'src/app/shared/services/modal.service';
import { Subscription } from 'rxjs';
import { ConfigurationService } from 'src/app/core';
import { NavigationEnd, Router } from '@angular/router';
import { AppConfiguration } from 'src/app/app.configuration';
import { AmigosThyssenService } from './services/amigos-thyssen.service';

@Component({
  selector: 'ticketing-amigos-thyssen',
  templateUrl: './amigos-thyssen.component.html',
  styleUrls: ['./amigos-thyssen.component.scss'],
  host: {
    class: 'layout'
  }
})
export class AmigosThyssenComponent implements OnInit, OnDestroy, AfterContentChecked {
  pasos = [];
  ventanaActualDePago: boolean;
  ventanaActualDeEntradas = true;
  entradasConMotivos = [];
  modoPresentacion: string;
  continuar: boolean;
  routerSub: Subscription;
  configSub: Subscription;
  stepperSub: Subscription;
  carritoSub: Subscription;
  private rutaAnterior: string = '';
  private subs: Subscription[] = [];

  constructor(
    private router: Router,
    private stepperNavigation: StepperNavigationService,
    private venta: VentaService,
    private modalService: ModalService,
    private config: ConfigurationService,
    private configuracion: AppConfiguration,
    private changeDetector: ChangeDetectorRef,
    private amigosThyssenService: AmigosThyssenService
  ) {
    
    this.routerSub = router.events.subscribe(val => {
      if (val instanceof NavigationEnd) {
        this.ventanaActualDePago =
          val.url.split('/')[2] === 'pago' ? true : false;
        this.ventanaActualDeEntradas =
          val.url.split('/')[2] === 'entradas' ? true : false;
        
        // Detectar si se está saliendo de amigos-thyssen
        this.detectarSalidaAmigosThyssen(val.url);
      }
    });
  }

  ngOnInit() {
    this.configSub = this.config.appConfig.subscribe(data => {
      this.modoPresentacion = data.DatosResult.AppConfigJsonConfig && data.DatosResult.AppConfigJsonConfig.ModoPresentacion ?
        data.DatosResult.AppConfigJsonConfig.ModoPresentacion : '';
    });
    
    this.stepperSub = this.stepperNavigation.continuarVenta.subscribe(data => {
      this.continuar = data;
    });
    
    // Configurar el modo de entradas para amigos thyssen
    this.venta.setMasEntradas('amigos-thyssen');
    
    // Limpiar estado al entrar al componente
    this.limpiarEstadoAmigosThyssen();
    
    // Guardar la ruta actual
    this.rutaAnterior = this.router.url;
    
    // Suscribirse a cambios en el carrito para verificar entradas de amigos thyssen
    this.carritoSub = this.venta.carrito.subscribe(carrito => {
      if (carrito) {
        this.verificarEntradasEnCarrito();
      }
    });
  }

  ngAfterContentChecked(): void {
    this.changeDetector.detectChanges();
  }

  ngOnDestroy() {
    if (this.routerSub) {
      this.routerSub.unsubscribe();
    }
    if (this.configSub) {
      this.configSub.unsubscribe();
    }
    if (this.stepperSub) {
      this.stepperSub.unsubscribe();
    }
    if (this.carritoSub) {
      this.carritoSub.unsubscribe();
    }
  }

  /**
   * Detecta si se está saliendo de la pantalla de amigos thyssen
   */
  private detectarSalidaAmigosThyssen(urlActual: string): void {
    const rutaActual = urlActual;
    
    // Si estábamos en amigos-thyssen y ahora no, limpiar estado
    if (this.rutaAnterior.includes('amigos-thyssen') && !rutaActual.includes('amigos-thyssen')) {
      this.limpiarEstadoAmigosThyssen();
    }
    
    // Actualizar la ruta anterior
    this.rutaAnterior = rutaActual;
  }

  /**
   * Verifica si hay entradas de amigos thyssen en el carrito
   * y limpia el estado si no hay ninguna
   */
  verificarEntradasEnCarrito(): void {
    const carrito = this.venta.carritoValue;
    const amigo = this.amigosThyssenService.getAmigoActivo();
    
    if (!carrito || !carrito.entradas || !amigo) return;
    
    // Contar entradas de amigos thyssen en el carrito
    const entradasAmigosThyssen = carrito.entradas.filter(entrada => 
      entrada.entrada.DNIAbonadosExt === amigo.namigo
    );
    
    // Solo limpiar el estado si no hay entradas de amigos thyssen Y estamos saliendo del módulo
    // No limpiar automáticamente cuando se agregan entradas
    if (entradasAmigosThyssen.length === 0 && this.rutaAnterior && !this.router.url.includes('amigos-thyssen')) {
      this.limpiarEstadoAmigosThyssen();
    }
  }

  /**
   * Limpia completamente el estado de amigos thyssen
   */
  limpiarEstadoAmigosThyssen(): void {
    // Limpiar el amigo activo
    this.amigosThyssenService.clearAmigoActivo();
    
    // Limpiar el cliente seleccionado
    this.venta.setClienteSeleccionadoValue('');
    
    // Limpiar el carrito si solo tiene entradas de amigos thyssen
    this.limpiarCarritoAmigosThyssen();
  }

  /**
   * Limpia el carrito de entradas específicas de amigos thyssen
   */
  private limpiarCarritoAmigosThyssen(): void {
    // NO limpiar el carrito automáticamente
    // El carrito debe mantenerse para permitir navegación entre secciones
    // Solo se limpiará cuando el usuario cancele explícitamente la venta
  }

  hayEntradasEnElCarrito(): boolean {
    return this.venta.carritoValue.entradas.length !== 0;
  }

  esReservaAgrupada(): boolean {
    return this.venta.carritoOrigenValue.reservaAgrupada === '1';
  }

  avanzar() {
    // Verificar si hay un amigo validado antes de continuar
    if (this.amigosThyssenService.getAmigoActivo()) {
      this.stepperNavigation.continuarVenta.next(true);
      this.stepperNavigation.next();
    }
  }

  openMasEntradasModal() {
    this.modalService.open('selector-mas-entradas');
  }

  isContinuar() {
    return this.continuar;
  }

  get hayValidacionExitosa(): boolean {
    const amigo = this.amigosThyssenService.getAmigoActivo();
    return amigo && amigo.iCodigoClienteVinculado ? true : false;
  }
}


